import { BRAND, SITE_ORIGIN } from "../brand";
import { LegalPage } from "../components/marketing-page";
import { marketingLinks, socialImageMeta } from "../components/marketing/layout";

export const links = marketingLinks;

export const meta = () => [
  { title: `Support & FAQ — ${BRAND.name} for Shopify` },
  {
    name: "description",
    content: `Get help with ${BRAND.name}, browse FAQs, and find answers about creating FDA-style Nutrition Facts and Supplement Facts panels in Shopify.`,
  },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/support` },
  { property: "og:url", content: `${SITE_ORIGIN}/support` },
  { property: "og:type", content: "website" },
  ...socialImageMeta(),
  {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `Support & FAQ — ${BRAND.name}`,
      url: `${SITE_ORIGIN}/support`,
      publisher: { "@type": "Organization", name: "Legitimate LLC" },
    },
  },
];

export default function Support() {
  return (
    <LegalPage title={`Support — ${BRAND.name}`}>
      {/* HOW TO GET HELP */}
      <h2>How to get help</h2>
      <p>
        We're here to help. The fastest way to reach us is by email:
      </p>

      <div className="nm-contactcard">
        <p>
          <a href={`mailto:${BRAND.supportEmail}`}>
            {BRAND.supportEmail}
          </a>
        </p>
        <p>
          We typically respond within <strong>1–2 business days</strong>.
        </p>
        <p className="nm-contactcard__fine">
          Please include your <code>.myshopify.com</code> domain
          and a description of what you were doing when the issue occurred —
          this helps us look up your session and resolve things faster.
        </p>
      </div>

      <p>
        Before writing in, browse the FAQ below — many common questions are
        answered there.
      </p>

      <hr />

      {/* FAQ */}
      <h2>Frequently asked questions</h2>

      {/* Q1 */}
      <div className="nm-faqitem">
        <h3>How do I create a Nutrition Facts label?</h3>
        <p>
          Open {BRAND.name} from your Shopify admin, then follow these steps:
        </p>
        <ol>
          <li>
            Click <strong>Select product</strong> and choose the product you
            want to label.
          </li>
          <li>
            Enter the serving size, servings per container, and all nutrient
            amounts in the fields provided.
          </li>
          <li>
            The Nutrition Facts panel builds <strong>live</strong> as you type
            — FDA 21 CFR 101.9 rounding is applied automatically.
          </li>
          <li>
            Review the panel for accuracy, then click{" "}
            <strong>Save label</strong> to store it to your product.
          </li>
        </ol>
        <div className="nm-notebox">
          Always verify the saved label against current FDA requirements before
          printing or listing for sale. See the{" "}
          <a href="/terms#disclaimer">
            disclaimer in our Terms of Service
          </a>{" "}
          for details.
        </div>
      </div>

      {/* Q2 */}
      <div className="nm-faqitem">
        <h3>Where is my label stored?</h3>
        <p>
          Your label is stored as a <strong>Shopify metaobject on your own store</strong>,
          attached to the product you labeled. This means:
        </p>
        <ul>
          <li>
            You own the data — it lives in your Shopify store, not only in our
            database.
          </li>
          <li>
            You can view, edit, or delete metaobjects directly from the Shopify
            admin under{" "}
            <strong>Content → Metaobjects</strong>.
          </li>
          <li>
            We also keep a lightweight activity record in our database (the
            timestamp and product ID) to power your in-app label history list.
            That record is deleted when you uninstall the app and 30 days have
            passed.
          </li>
        </ul>
      </div>

      {/* Q3 */}
      <div className="nm-faqitem">
        <h3>Can I export the label as an image?</h3>
        <p>
          Yes. Open any saved label from your history list. You will see an{" "}
          <strong>Export</strong> button that downloads the label as a{" "}
          print-ready <strong>PNG image</strong> suitable for packaging artwork
          or digital listings.
        </p>
        <p style={{ marginTop: "0.75rem" }}>
          For best print results, check with your packaging vendor for minimum
          resolution requirements. If you need a vector format (SVG, PDF),
          contact us at{" "}
          <a href={`mailto:${BRAND.supportEmail}`}>
            {BRAND.supportEmail}
          </a>{" "}
          and we can discuss your needs.
        </p>
      </div>

      {/* Q4 */}
      <div className="nm-faqitem">
        <h3>
          Is the label guaranteed to comply with FDA regulations?
        </h3>
        <p>
          <strong>No.</strong> {BRAND.name} is a calculation and formatting
          tool — it applies FDA 21 CFR 101.9 rounding rules to the values{" "}
          <em>you</em> provide. It does not independently verify your input
          data, perform laboratory analysis, or guarantee regulatory compliance.
        </p>
        <p style={{ marginTop: "0.75rem" }}>
          You are responsible for:
        </p>
        <ul>
          <li>
            Accuracy of all nutrient values you enter (these should come from a
            certified lab analysis or a validated recipe calculation).
          </li>
          <li>
            Correct allergen statements (not generated by the tool).
          </li>
          <li>
            Compliance with current FDA guidance, which may change after our
            last update.
          </li>
        </ul>
        <div className="nm-notebox">
          Read the full disclaimer in our{" "}
          <a href="/terms">
            Terms of Service
          </a>{" "}
          before using labels commercially. We strongly recommend review by a
          qualified regulatory professional.
        </div>
      </div>

      {/* Q5 */}
      <div className="nm-faqitem">
        <h3>How do I remove my data from {BRAND.name}?</h3>
        <p>
          You have several options depending on what you want to remove:
        </p>
        <ul>
          <li>
            <strong>Remove all data immediately:</strong> Use the{" "}
            <strong>"Remove all data"</strong> control inside the app (Settings
            page). This deletes all label metaobjects from your store and all
            activity records from our database.
          </li>
          <li>
            <strong>Remove a single label:</strong> Open the label from your
            history and click <strong>Delete label</strong>.
          </li>
          <li>
            <strong>Uninstall the app:</strong> Uninstalling{" "}
            {BRAND.name} from the Shopify admin triggers automatic deletion of
            your activity records from our database within 30 days. Metaobject
            data on your store is not automatically removed (it stays with your
            store), but you can delete those via Shopify admin under{" "}
            <strong>Content → Metaobjects</strong>.
          </li>
          <li>
            <strong>Shopify-initiated deletion:</strong> We automatically honor
            Shopify's{" "}
            <code>customers/redact</code> and{" "}
            <code>shop/redact</code> webhooks within 30 days.
          </li>
        </ul>
        <p style={{ marginTop: "0.75rem" }}>
          Questions about data deletion? Email{" "}
          <a href={`mailto:${BRAND.supportEmail}`}>
            {BRAND.supportEmail}
          </a>
          .
        </p>
      </div>

      {/* Q6 */}
      <div className="nm-faqitem">
        <h3>
          The app isn't loading in my Shopify admin — what should I try?
        </h3>
        <p>
          Try the following in order:
        </p>
        <ol>
          <li>
            Reload the page with a hard refresh (
            <code>Cmd+Shift+R</code> on Mac,{" "}
            <code>Ctrl+Shift+R</code> on Windows).
          </li>
          <li>
            Open the app in a different browser to rule out a browser extension
            conflict.
          </li>
          <li>
            Check that your browser allows third-party cookies (required for
            Shopify embedded apps to pass session tokens).
          </li>
          <li>
            Uninstall and reinstall {BRAND.name} from the Shopify App Store.
            Your saved labels (stored as metaobjects on your store) will be
            preserved.
          </li>
        </ol>
        <p style={{ marginTop: "0.75rem" }}>
          If none of the above resolves the issue, email{" "}
          <a href={`mailto:${BRAND.supportEmail}`}>
            {BRAND.supportEmail}
          </a>{" "}
          with your{" "}
          <code>.myshopify.com</code> domain, your browser and
          version, and a description of what you see.
        </p>
      </div>

      <hr />

      {/* LINKS */}
      <h2>Legal</h2>
      <p>
        <a href="/privacy">
          Privacy Policy
        </a>{" "}
        — how we collect, use, and protect your data.
      </p>
      <p>
        <a href="/terms">
          Terms of Service
        </a>{" "}
        — the rules governing your use of {BRAND.name}, including the
        important calculation disclaimer.
      </p>

      <hr />

      {/* CONTACT REPEAT */}
      <h2>Still need help?</h2>
      <p>
        Email us at{" "}
        <a href={`mailto:${BRAND.supportEmail}`}>
          {BRAND.supportEmail}
        </a>
        . Include your store domain and a clear description of the issue and we
        will respond within 1–2 business days.
      </p>
    </LegalPage>
  );
}
